import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import i18n from "i18next";
import SideBar from "../SideBar";
import TopBar from "../TopBar";
import { AuditEntry, CapaStatus, Classification, FindingReviewStatus, RiskLevel, AnalysisStatus } from "../../types/ai";
import { secureFileUrl } from "../../utils/secureFile";
import "../../styles/aces.css";

/** Locale of the app language (the web app calls Spanish "sp"). */
export const appLocale = () => {
  const lng = (i18n.language || "en").slice(0, 2);
  return lng === "sp" ? "es" : lng;
};

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();

/** Date and time in the user's app language. */
export const fmtDate = (v?: string | null) => {
  if (!v) return "—";
  const loc = appLocale();
  if (!dateTimeFormats.has(loc)) dateTimeFormats.set(loc, new Intl.DateTimeFormat(loc, { dateStyle: "medium", timeStyle: "short" }));
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : dateTimeFormats.get(loc)!.format(d);
};

/** Calendar date in the user's app language: a YYYY-MM-DD value as written, a timestamp in the user's time zone. */
export const fmtDay = (v?: string | null) => {
  if (!v) return "—";
  const loc = appLocale();
  if (v.length > 10) {
    const instant = new Date(v);
    return isNaN(instant.getTime()) ? v : new Intl.DateTimeFormat(loc, { dateStyle: "medium" }).format(instant);
  }
  if (!dateFormats.has(loc)) dateFormats.set(loc, new Intl.DateTimeFormat(loc, { dateStyle: "medium", timeZone: "UTC" }));
  const d = new Date(`${v.slice(0, 10)}T00:00:00Z`);
  return isNaN(d.getTime()) ? v : dateFormats.get(loc)!.format(d);
};

export interface Crumb {
  label: React.ReactNode;
  to?: string;
}

export const Breadcrumbs: React.FC<{ items: Crumb[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
    <nav className="aces-breadcrumbs" aria-label={t("aces.a11y.breadcrumb")}>
      {items.map((c, i) => (
        <span key={i}>
          {i > 0 && <span aria-hidden="true"> › </span>}
          {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
        </span>
      ))}
    </nav>
  );
};

/** Standard dashboard frame (sidebar + top bar) used by every QualiCheck AI screen. */
export const PageShell: React.FC<{ title: React.ReactNode; actions?: React.ReactNode; crumbs?: Crumb[]; children: React.ReactNode }> = ({ title, actions, crumbs, children }) => (
  <div className="__dashboard">
    <div className="__dashboard__wrapper">
      <SideBar />
      <div className="__dashboardRightSide">
        <TopBar />
        <div className="__dashboardContent">
          {crumbs && <Breadcrumbs items={crumbs} />}
          <div className="__pageHeading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>{title}</span>
            {actions}
          </div>
          <div className="__myTraining">{children}</div>
        </div>
      </div>
    </div>
  </div>
);

export const Loading: React.FC<{ text?: string }> = ({ text }) => (
  <div style={{ textAlign: "center", padding: 30 }}>
    <i className="ri-loader-4-line ri-spin" style={{ fontSize: 22 }}></i> {text}
  </div>
);

const CLASSIFICATION_COLOR: Record<Classification, string> = {
  CONFORMITY: "green",
  NOT_DEMONSTRATED: "amber",
  OPPORTUNITY_FOR_IMPROVEMENT: "blue",
  OBSERVATION: "grey",
  MINOR: "amber",
  MAJOR: "orange",
  CRITICAL: "darkred",
};
const RISK_COLOR: Record<RiskLevel, string> = { LOW: "green", MODERATE: "amber", HIGH: "orange", CRITICAL: "darkred", NOT_DETERMINED: "grey" };
const REVIEW_COLOR: Record<FindingReviewStatus, string> = {
  AI_PROPOSED: "blue", UNDER_REVIEW: "amber", ACCEPTED: "green", MODIFIED: "green", REJECTED: "grey", CLOSED: "grey",
};
const ANALYSIS_COLOR: Record<AnalysisStatus, string> = {
  QUEUED: "grey", PROCESSING: "blue", DRAFT_READY: "amber", FAILED: "red", UNDER_REVIEW: "amber", REVIEWED: "green", SUPERSEDED: "grey",
};
const capaColor = (s: CapaStatus) =>
  s === "CLOSED" ? "green" : s === "REJECTED" ? "grey" : s === "REOPENED" ? "red" : s === "AI_PROPOSED" ? "blue" : "amber";

export const ClassificationChip: React.FC<{ value: Classification }> = ({ value }) => {
  const { t } = useTranslation();
  return <span className={`aces-chip ${CLASSIFICATION_COLOR[value]}`}>{t(`aces.enum.classification.${value}`)}</span>;
};
export const RiskChip: React.FC<{ level: RiskLevel; score?: number | null }> = ({ level, score }) => {
  const { t } = useTranslation();
  return (
    <span className={`aces-chip ${RISK_COLOR[level]}`}>
      {score != null ? `${score} · ` : ""}
      {t(`aces.enum.risk.${level}`)}
    </span>
  );
};
export const ReviewChip: React.FC<{ value: FindingReviewStatus }> = ({ value }) => {
  const { t } = useTranslation();
  return <span className={`aces-chip ${REVIEW_COLOR[value]}`}>{t(`aces.enum.review.${value}`)}</span>;
};
export const AnalysisStatusChip: React.FC<{ value: AnalysisStatus }> = ({ value }) => {
  const { t } = useTranslation();
  return <span className={`aces-chip ${ANALYSIS_COLOR[value]}`}>{t(`aces.enum.analysis.${value}`)}</span>;
};
export const CapaChip: React.FC<{ value: CapaStatus }> = ({ value }) => {
  const { t } = useTranslation();
  return <span className={`aces-chip ${capaColor(value)}`}>{t(`aces.enum.capa.${value}`)}</span>;
};

/** requirements.md §7 banner. */
export const UrgentBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="aces-urgent" role="alert">
      <i className="ri-alarm-warning-fill" aria-hidden="true"></i> {t("aces.urgentBanner")}
    </div>
  );
};

/** Shown wherever AI output is not yet a human decision. */
export const AiNotice: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="aces-ai-notice">
      <i className="ri-sparkling-2-line" aria-hidden="true"></i> {t("aces.aiNotice")}
    </div>
  );
};

export const PAGE_SIZES = [25, 50];

/** Previous/next and page size for client-side or server-side lists (page is 0-based). */
export const Pager: React.FC<{ page: number; size: number; total: number; onPage: (p: number) => void; onSize: (s: number) => void }> = ({ page, size, total, onPage, onSize }) => {
  const { t } = useTranslation();
  const last = Math.max(0, Math.ceil(total / size) - 1);
  return (
    <div className="aces-pager">
      <label className="aces-actions">
        {t("aces.pager.perPage")}
        <select className="form-control form-control-sm" value={size} onChange={(e) => onSize(Number(e.target.value))}>
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <span aria-live="polite">{t("aces.pager.summary", { from: total === 0 ? 0 : page * size + 1, to: Math.min((page + 1) * size, total), total })}</span>
      <div className="aces-actions">
        <button type="button" className="aces-btn small secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>{t("aces.pager.prev")}</button>
        <button type="button" className="aces-btn small secondary" disabled={page >= last} onClick={() => onPage(page + 1)}>{t("aces.pager.next")}</button>
      </div>
    </div>
  );
};

/** Image from tenant-private storage (fetched with the session token). */
export const SecureImage: React.FC<{ fileId: number; alt?: string; className?: string }> = ({ fileId, alt, className }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let active = true;
    secureFileUrl(fileId).then((url) => active && setSrc(url)).catch(() => active && setSrc(null));
    return () => {
      active = false;
    };
  }, [fileId]);
  if (!src) return <div className={className || "aces-photo"} />;
  return (
    <>
      <button type="button" className="aces-photo-btn" aria-label={alt} onClick={() => setZoom(true)}>
        <img src={src} alt={alt || ""} title={alt} className={className || "aces-photo"} />
      </button>
      <Modal show={zoom} onHide={() => setZoom(false)} centered size="xl">
        <Modal.Header closeButton>{alt}</Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          <img src={src} alt={alt || ""} style={{ maxWidth: "100%", maxHeight: "75vh" }} />
        </Modal.Body>
      </Modal>
    </>
  );
};

/** Asks for the mandatory reason of a material change or transition. */
export const ReasonModal: React.FC<{
  show: boolean;
  title: string;
  required?: boolean;
  confirmLabel?: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  children?: React.ReactNode;
}> = ({ show, title, required = true, confirmLabel, danger, onCancel, onConfirm, children }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (show) setReason("");
  }, [show]);
  return (
    <Modal show={show} onHide={onCancel} centered className="__modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {children}
        <div className="aces-field">
          <label htmlFor="aces-reason">
            {t("aces.common.reason")} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <textarea id="aces-reason" className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} autoFocus />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="aces-btn secondary" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button
          className={`aces-btn ${danger ? "danger" : ""}`}
          disabled={busy || (required && !reason.trim())}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(reason.trim());
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy && <i className="ri-loader-4-line ri-spin"></i>}
          {confirmLabel || t("aces.common.confirm")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export const ErrorAlert: React.FC<{ message: string | null; onClose?: () => void }> = ({ message, onClose }) => {
  const { t } = useTranslation();
  return message ? (
    <div className="aces-error" role="alert" style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
      <span>{message}</span>
      {onClose && (
        <button type="button" className="aces-icon-btn" aria-label={t("aces.a11y.close")} onClick={onClose}>
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      )}
    </div>
  ) : null;
};

/** Record → Previous → New → User → Date/Time → Reason (requirements.md §9). */
export const AuditTable: React.FC<{ entries: AuditEntry[] }> = ({ entries }) => {
  const { t } = useTranslation();
  if (!entries.length) return <p className="aces-muted">{t("aces.audit.empty")}</p>;
  return (
    <div className="__defaultTable">
      <table>
        <thead>
          <tr>
            <th>{t("aces.audit.when")}</th>
            <th>{t("aces.audit.record")}</th>
            <th>{t("aces.audit.action")}</th>
            <th>{t("aces.audit.field")}</th>
            <th>{t("aces.audit.previous")}</th>
            <th>{t("aces.audit.new")}</th>
            <th>{t("aces.audit.user")}</th>
            <th>{t("aces.audit.reason")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((a) => (
            <tr key={a.id}>
              <td>{fmtDate(a.createdAt)}</td>
              <td>{a.entityRef || `${a.entityType} #${a.entityId}`}</td>
              <td>{a.action}</td>
              <td>{a.field || "—"}</td>
              <td className="aces-preline">{a.previousValue ?? "—"}</td>
              <td className="aces-preline">{a.newValue ?? "—"}</td>
              <td>{a.userName || "—"}</td>
              <td className="aces-preline">{a.reason || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
